import { FileValidationStatus, RejectedFile, AttachmentDto, FormFieldDto } from '../types';
import { formatTimestamp } from '@/helpers';
import { BASE_FORM_VALUE_KEYS, MAX_ALLOWED_FILE_UPLOAD_COUNT } from '@/features/issuesPage/constants';

export const convertFilesToAttachments = (fileAttachments: (File & { content: number[] })[]): AttachmentDto[] =>
    fileAttachments.map(file => ({
        fileName: file.name,
        contentType: file.type,
        content: file.content,
        id: crypto.randomUUID()
    }));

export const convertFilesWithBytes = async (files: File[]): Promise<(File & { content: string })[]> => {
    const readFile = async (file: File): Promise<File & { content: string }> => {
        const reader = new FileReader();
        const content = await new Promise<string>(resolve => {
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(file);
        });
        return Object.assign(file, { content: content.split(',')[1] });
    };

    return Promise.all(files.map(readFile));
};

export const downloadFile = (file: Blob, filename: string): void => {
    const url = URL.createObjectURL(file);
    Object.assign(document.createElement('a'), {
        href: url,
        download: filename
    }).click();
    URL.revokeObjectURL(url);
};

export const validateFiles = async (
    files: File[],
    accept: string,
    maxSize: number,
    failedSizeError: string,
    failedExtensionError: string,
    failedMaxCountError: string
): Promise<FileValidationStatus> => {
    const result: FileValidationStatus = {
        valid: [],
        rejected: []
    };

    const maxSizeMb = getFileSize(maxSize);

    const validatedFiles = files.map((file, index) => {
        const fileType = `.${file.name.split('.').pop()}`.toLowerCase();
        const acceptedTypes = accept.split(',');
        const isValidType = acceptedTypes.includes(fileType);
        const isValidSize = file.size <= maxSize;
        const isValidMaxCount = index < MAX_ALLOWED_FILE_UPLOAD_COUNT;

        if (isValidType && isValidSize && isValidMaxCount) {
            return { valid: file };
        }

        const reason = !isValidType
            ? `${fileType} - ${failedExtensionError}`
            : !isValidMaxCount
              ? failedMaxCountError
              : `${failedSizeError} ${maxSizeMb}`;

        return {
            rejected: {
                name: file.name,
                reason
            }
        };
    });

    result.valid = validatedFiles.filter(f => f.valid).map(f => f.valid!);
    result.rejected = validatedFiles.filter(f => f.rejected).map(f => f.rejected!);

    return result;
};

export const rejectedFileNotification = (rejectedFiles: RejectedFile[]) =>
    rejectedFiles.map(file => `${file.name}: ${file.reason}`).join(' | ');

export const getFileSize = (size: number): string => `${size / (1024 * 1024)}MB`;

export const getFormDateByTimeZone = (timeZone: string): FormFieldDto => {
    return {
        type: 'DateTime',
        key: BASE_FORM_VALUE_KEYS.OCCURRED_DATE,
        value: formatTimestamp(Date.now(), 'dd.MM.yyyy HH:mm', { timeZone })
    };
};
